import React from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Grid,
  CircularProgress,
  Box,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import {
  OutageDetailsByCounty,
  ZipCodeInfo,
} from '../../../../__generated__/pge-types';
import OutagePanelLayout from '../outage-map-panel/outage-panel-layout/OutagePanelLayout';
import { DrawerSnapType } from '../bottom-drawer-layout/BottomDrawerLayout';
import useContentMgmt from '../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../pge-plus-common/microcopyGroupIds';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    blueCard: {
      background: colors.modalBlue,
      boxShadow: 'none',
      padding: '1.25rem',
    },
    outageAccountsHeader: {
      fontSize: '1.375rem',
      fontWeight: 'bold',
    },
    outageNote: {
      color: colors.darkGray1,
      margin: theme.spacing(0, 0, 1.25, 0),
    },
    outageName: {
      color: colors.brightBlue,
      fontWeight: 'bold',
    },
    outageLabel: {
      color: colors.noirBlur,
      '&:first-child': {
        margin: theme.spacing(0, 2.5, 0, 0),
      },
    },
    labelWrap: {
      margin: theme.spacing(0, 0, 1.25, 0),
      padding: theme.spacing(0, 0, 1.25, 0),
      borderBottom: `1px solid ${colors.cloudGrey}`,
      '&:last-child': {
        margin: 0,
        padding: 0,
        borderColor: 'transparent',
      },
    },
    outageListWrap: {
      maxHeight: '400px',
      overflow: 'auto',
    },
    accountsInfoNote: {
      fontSize: '0.75rem',
      color: colors.noirBlur,
      margin: theme.spacing(0, 0, 2, 0),
    },
    accountsTitle: {
      margin: theme.spacing(0, 0, 2, 0),
    },
    accountName: {
      color: colors.brightBlue,
      margin: theme.spacing(0, 1, 0, 0),
    },
    outageReportedLabel: {
      color: colors.errorRed,
    },
    accountWrap: {
      margin: theme.spacing(0, 0, 1, 0),
    },
    accountLabelWrap: {
      borderColor: colors.white,
    },
    zipWrap: {
      margin: theme.spacing(1, 0, 0, 0),
    },
    nextOutageHeader: {
      '& p': {
        margin: 0,
      },
    },
    noOutagesError: {
      margin: '10px',
    },
    closeBtn: {
      position: 'absolute',
      top: '8px',
      right: '15px',
    },
  }),
);

interface Props {
  totalOutages: number;
  outageByCounty?: OutageDetailsByCounty[];
  handleZipcodeClick: (
    countyName: string | null | undefined,
    zipCode?: string | null | undefined,
    zipIndex?: number | null | undefined,
    zipCodes?: Array<ZipCodeInfo> | undefined | null,
  ) => void;
  isOutageByCountyLoading: boolean;
  isOutageByCountyError: boolean;
  handleMobileOutageListPanel?: () => void;
}

export const OutageCountyListPanel = (props: Props) => {
  const {
    totalOutages,
    outageByCounty,
    handleZipcodeClick,
    isOutageByCountyLoading,
    isOutageByCountyError,
    handleMobileOutageListPanel,
  } = props;

  const classes = useStyles();
  const { t } = useTranslation();

  const { content } = useContentMgmt(
    microcopyGroupIds.OUTAGES_CUSTOMERS_IMPACTED_BY_COUNTY,
  );

  const { content: countyContent } = useContentMgmt(
    microcopyGroupIds.OUTAGES_BY_COUNTY,
  );

  const customerImpactedfunc = (totalCustomersImpacted: any, county: any) => {
    if (county === 'CLACKAMAS') {
      return (
        content.get('CLACKAMAS_COUNTY_CUSTOMERS_IMPACTED') ||
        totalCustomersImpacted
      );
    }
    if (county === 'WASHINGTON') {
      return (
        content.get('WASHINGTON_COUNTY_CUSTOMERS_IMPACTED') ||
        totalCustomersImpacted
      );
    }
    if (county === 'MULTNOMAH') {
      return (
        content.get('MULTNOMAH_COUNTY_CUSTOMERS_IMPACTED') ||
        totalCustomersImpacted
      );
    }
    if (county === 'MARION') {
      return (
        content.get('MARION_COUNTY_CUSTOMERS_IMPACTED') ||
        totalCustomersImpacted
      );
    }
    if (county === 'YAMHILL') {
      return (
        content.get('YAMHILL_COUNTY_CUSTOMERS_IMPACTED') ||
        totalCustomersImpacted
      );
    }
    if (county === 'COLUMBIA') {
      return (
        content.get('COLUMBIA_COUNTY_CUSTOMERS_IMPACTED') ||
        totalCustomersImpacted
      );
    }
    if (county === 'POLK') {
      return (
        content.get('POLK_COUNTY_CUSTOMERS_IMPACTED') || totalCustomersImpacted
      );
    } else {
      return totalCustomersImpacted;
    }
  };

  const customerOutagefunc = (outages: any, county: any) => {
    if (county === 'CLACKAMAS') {
      return countyContent.get('CLACKAMAS_COUNTY_OUTAGES') || outages;
    }
    if (county === 'WASHINGTON') {
      return countyContent.get('WASHINGTON_COUNTY_OUTAGES') || outages;
    }
    if (county === 'MULTNOMAH') {
      return countyContent.get('MULTNOMAH_COUNTY_OUTAGES') || outages;
    }
    if (county === 'MARION') {
      return countyContent.get('MARION_COUNTY_OUTAGES') || outages;
    }
    if (county === 'YAMHILL') {
      return countyContent.get('YAMHILL_COUNTY_OUTAGES') || outages;
    }
    if (county === 'COLUMBIA') {
      return countyContent.get('COLUMBIA_COUNTY_OUTAGES') || outages;
    }
    if (county === 'POLK') {
      return countyContent.get('POLK_COUNTY_OUTAGES') || outages;
    } else {
      return outages;
    }
  };

  return (
    <OutagePanelLayout refId={3} defaultSnap={DrawerSnapType.default}>
      <Typography className={classes.outageAccountsHeader}>{`${t(
        'OUTAGES',
      )} (${totalOutages})`}</Typography>
      <Box className={classes.closeBtn}>
        <IconButton onClick={handleMobileOutageListPanel}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography variant="subtitle1" className={classes.outageNote}>
        {`* ${t('OUTAGE_CENTER_MAP_PANEL_NOTE')}`}
      </Typography>
      {isOutageByCountyLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <Grid className={classes.outageListWrap}>
          {outageByCounty &&
            outageByCounty
              .sort((a, b) => (a.county < b.county ? -1 : 1))
              .map((outage: OutageDetailsByCounty, index: number) => {
                const {
                  county,
                  outages,
                  totalCustomersImpacted,
                  zipCodeInfo,
                } = outage;
                return (
                  <React.Fragment key={`${county}-${index}`}>
                    <Typography
                      onClick={e => {
                        e.preventDefault();
                        handleZipcodeClick(county, null, null, zipCodeInfo);
                      }}
                      variant="body1"
                      className={classes.outageName}
                    >
                      {county}
                    </Typography>
                    <Grid container className={classes.labelWrap}>
                      <Grid item>
                        <Typography
                          className={classes.outageLabel}
                          variant="h6"
                        >
                          {`${t('OUTAGE(S)')}: `}
                          <strong>{customerOutagefunc(outages, county)}</strong>
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          className={classes.outageLabel}
                          variant="h6"
                        >
                          {`${t('CUSTOMER(S)')}: `}
                          <strong>
                            {customerImpactedfunc(
                              totalCustomersImpacted,
                              county,
                            )}
                          </strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                );
              })}
        </Grid>
      )}
      {isOutageByCountyError ? (
        <Typography className={classes.noOutagesError}>
          {' '}
          No reported Outages
        </Typography>
      ) : null}
    </OutagePanelLayout>
  );
};

export default OutageCountyListPanel;
